import { useRef, useState, Children, cloneElement, isValidElement } from "react";
import { flags } from "@/config/featureFlags";
import useMotionPreferences from "@/hooks/useMotionPreferences";

/**
 * ListingCard3D — wraps any existing card DOM with a lightweight 3D-tilt +
 * glow-edge effect. Never rewrites the card content; it just enhances it.
 *
 * Usage:
 *   <ListingCard3D business={b}>
 *     <Link to="..." className="glass-card ..."> ...existing card... </Link>
 *   </ListingCard3D>
 *
 * Fallbacks:
 *   - Flag off: renders children verbatim.
 *   - prefers-reduced-motion or coarse-pointer (touch): no tilt, no follow;
 *     just applies a subtle glow-edge class for best_of_best.
 *   - Never uses WebGL; pure CSS transforms.
 */
export default function ListingCard3D({
  business = {},
  children,
  intensity = 8, // max tilt degrees
  className = "",
  testid = "listing-card-3d",
}) {
  const enabled = flags.ENABLE_3D_LISTING_CARDS;
  const { prefersReducedMotion, isTouch } = useMotionPreferences();
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  if (!enabled) return <>{children}</>;

  const isBoB = Boolean(business?.is_best_of_best || business?.is_ai_certified);
  const canTilt = !prefersReducedMotion && !isTouch;

  const handleMove = (e) => {
    if (!canTilt || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setStyle({
      transform: `perspective(900px) rotateX(${-y * intensity}deg) rotateY(${x * intensity}deg) translateZ(0)`,
      transition: "transform 60ms ease-out",
    });
  };

  const handleLeave = () => {
    setStyle({
      transform: "perspective(900px) rotateX(0) rotateY(0) translateZ(0)",
      transition: "transform 400ms ease-out",
    });
  };

  // Some parents rely on the child being a Link (for keyboard focus + click).
  // We wrap in a div and preserve the child untouched underneath.
  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`listing-card-3d-wrap relative ${isBoB ? "listing-card-3d-bob" : ""} ${className}`}
      style={{ ...style, transformStyle: "preserve-3d", willChange: canTilt ? "transform" : undefined }}
      data-testid={testid}
    >
      {/* Glow edge for best_of_best listings (also visible on touch / reduced-motion) */}
      {isBoB && (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-2xl"
          style={{
            background: "linear-gradient(135deg, rgba(245,158,11,0.55), rgba(251,191,36,0.15) 40%, transparent 65%)",
            filter: "blur(2px)",
            opacity: 0.65,
            zIndex: -1,
          }}
        />
      )}

      {/* Render the original card DOM untouched underneath */}
      {Children.map(children, (child) =>
        isValidElement(child)
          ? cloneElement(child, {
              className: `${child.props.className || ""} relative`,
            })
          : child,
      )}
    </div>
  );
}
